import React, { useEffect, useMemo, useState } from 'react';
import TimeRowGroup from '../../types/TimePeriodGroupM';
import { PeriodDay } from '../../types/PeriodDay';
import MyTimeAdd from '../MyTimeAdd';
import { TimeGridMode } from '../../types/TimeGridMode';
import { DirectionalHint, FontIcon, Stack, Text, TooltipHost } from '@fluentui/react';
import { GetTotalFromTimeRowArray, FormatValue } from '../Utils';
import MyTimeDelete from '../MyTimeDelete';
import { useCellCtx } from '../Contexts/CellContext';
import ProjectSourceIcon from '../../Icons/ProjectSourceIcon';
import CellWeek from './CellWeek';
import { DisplayCell } from './DisplayCell';
import { EditableRemainingWorkCell } from './EditableRemainingWorkCell';
import { PlannedCell } from '../PlannedCell';
import { WorkTypePicker } from '../WorkTypePicker';
import { TimeEntry, UserRegistrationPeriod, UserReportingPeriodState, WorkType } from '../../api/generated/data-contracts';
import { useGridContext } from '../Contexts/GridContext/context';
import { useConfigValuesState } from '../../hooks/useConfigValue';
import { useIsCellReadOnly } from '../../helpers/cellReadOnly';
import { getPeriodDaysEnabled } from '../../helpers/periodDays';

interface propsTimePeriodGroup {
    group: TimeRowGroup;

    expanded: boolean;
    onCellWeekUpdated: (
        time: UserRegistrationPeriod,
        periodDays: PeriodDay[],
        te: any,
        cellValue: number,
        allCellsValue: number,
        comment: string,
        flagged: boolean,
        prevVal: number,
    ) => Promise<string[]>;
    mode: TimeGridMode;
    pinMyTime: Function;
    deleteMyTime: Function;
    today: Date;
    submitted: boolean;
    periodIsClosed: boolean;
    // timeLowerLimitConfig: number;
    startDate: Date;
    endDate: Date;
    deadlineDate: Date;
    perioddays: PeriodDay[];
    reportPeriodId: string;
    updateWorkType: (row: UserRegistrationPeriod, workType: WorkType, changeCompleted: Function) => void;
    timesheetstate: UserReportingPeriodState;
    onRemainingWorkUpdate?: (assignmentId: string, newRemainingWork: number) => void;
}

function TimePeriodGroupWeek(props: propsTimePeriodGroup) {
    const [times, setTimes] = useState<UserRegistrationPeriod[]>([]);
    // const [deletedTimeRows, setDeletedTimeRows] = useState<TimeRow[]>([]);
    const [expanded, setExpanded] = useState<boolean>(true);
    const [expandMiddleman, setExpandMiddleman] = useState<boolean>(true);
    const [groupiconrotation, setGroupiconrotation] = useState<string>('rotate(-90deg)');

    let timesheetIsReadonly = false;

    if (props.submitted) timesheetIsReadonly = true;
    if (props.periodIsClosed) timesheetIsReadonly = true;
    if (props.timesheetstate === 'Rejected') timesheetIsReadonly = false;

    const [cellDataNeedsSave, setCellDataNeedsSave] = useState<boolean>(false);

    //TODO FIX:
    const [updateTotal, setUpdateTotal] = useState<boolean>(true);

    // const uiCtx = useUiContext();
    const cellCtx = useCellCtx();

    const { actualActive, plannedActive, remainingActive } = useGridContext();

    const {
        initialData: {
            configuration: {
                timeSheetValidation: { timeEntriesMustHaveWorkType },
                workTypesEnabled,
                onlyAllowTimeSheetWithinTaskStartEnd,
                showPlanned,
                gridInputType,
                weekConfig,
                remainingWorkWritable,
            },
            aggregatedUser,
            allowedWorkTypes,
            projectTypes,
        },
    } = useConfigValuesState();

    const { getIfReadOnly } = useIsCellReadOnly({
        timesheetIsReadonly,
        onlyAllowTimeSheetWithinTaskStartEnd,
        periodDays: props.perioddays,
        aggregatedUser,
        workTypesEnabled,
        timeEntriesMustHaveWorkType,
        allowedWorkTypes,
        projectTypes,
    });

    useEffect(() => {
        setTimes([...props.group.TimeRows]);
    }, [props.group, updateTotal]);

    useEffect(() => {
        // setExpanded(props.expanded);
        setExpandMiddleman(props.expanded);
    }, [props.expanded]);

    useEffect(() => {
        if (expanded) {
            setGroupiconrotation('rotate(0deg)');
        } else {
            setGroupiconrotation('rotate(-90deg)');
        }
    }, [expanded]);

    useEffect(() => {
        if (!cellDataNeedsSave) {
            setExpanded(expandMiddleman);
        }
    }, [expandMiddleman, cellDataNeedsSave]);

    const groupGollapsedExpanded = (ev: React.MouseEvent<HTMLTableRowElement, MouseEvent>): void => {
        setExpandMiddleman(!expandMiddleman);
    };

    const onCellUpdated = (
        time: UserRegistrationPeriod,
        day: PeriodDay[],
        te: TimeEntry,
        cellValue: number,
        allCellsValue: number,
        comment: string,
        flagged: boolean,
        previousValue: number,
    ): Promise<string[]> => {
        // Update local times state to reflect actualWork changes
        const actualWorkDelta = allCellsValue - previousValue;
        setTimes(currentTimes =>
            currentTimes.map(t => (t.assignmentId === time.assignmentId ? { ...t, actualWork: (t.actualWork || 0) + actualWorkDelta } : t)),
        );

        setUpdateTotal(t => !t);
        return props.onCellWeekUpdated(time, day, te, cellValue, allCellsValue, comment, flagged, previousValue);
    };

    const PinMyTime = (time: UserRegistrationPeriod, pinned: boolean) => {
        props.pinMyTime(time, pinned);
    };

    const DeleteMyTime = (time: UserRegistrationPeriod) => {
        // setTimes(currentArray => [...currentArray.filter(c => c != time)])
        // setDeletedTimeRows(currentArray => [...currentArray, time]);
        props.deleteMyTime(time);
    };

    const setCellDataNeedsSaveFunc = (val: boolean) => {
        setCellDataNeedsSave(val);
    };

    const handleRemainingWorkUpdate = (assignmentId: string, newRemainingWork: number) => {
        // Update local state
        setTimes(currentTimes => currentTimes.map(time => (time.assignmentId === assignmentId ? { ...time, remainingWork: newRemainingWork } : time)));

        // Notify parent component to update its state as well
        if (props.onRemainingWorkUpdate) {
            props.onRemainingWorkUpdate(assignmentId, newRemainingWork);
        }
    };

    const workingDays = useMemo(() => {
        return getPeriodDaysEnabled(props.perioddays, null, weekConfig);
    }, [props.perioddays, weekConfig]);

    return (
        <>
            <tr className={`groupHeadRow`} onClick={groupGollapsedExpanded}>
                <td className="rowHeader noselect">
                    <Stack horizontal verticalAlign="center" className="rowHeaderContainer">
                        <Stack.Item className="expander">
                            <div className="expanderInner">
                                <FontIcon
                                    iconName="ChevronDown"
                                    style={{
                                        transform: groupiconrotation,
                                        transition: 'transform .2s ease 0s',
                                    }}
                                />
                            </div>
                        </Stack.Item>
                        <Text className="groupName textOverflow">{props.group.GroupName}</Text>
                        {props.group?.ProjectSource?.iconUrl && (
                            <Stack.Item>
                                <ProjectSourceIcon
                                    id={`${props.group.GroupName}_${props.group.ProjectSource.name}`}
                                    projectSource={props.group.ProjectSource}
                                />
                            </Stack.Item>
                        )}
                    </Stack>
                </td>

                <td className="cellContainer worktypeContainer noselect" hidden={!workTypesEnabled}>
                    <Stack horizontal className="cell day">
                        <Text className="groupName textOverflow"> {/* White space for work type (Strange layout if empty...) */} </Text>
                    </Stack>
                </td>

                {plannedActive && showPlanned && gridInputType !== 'percent' && (
                    <td className={`rowTotalContainer noselect`}>
                        <div className="rowTotal totalCell">
                            <Text className="totalCellInner" as="div">
                                <DisplayCell
                                    value={times?.reduce((prev, cur) => prev + (cur.actualWork || 0) + (cur.remainingWork || 0), 0)}
                                    periodDays={props.perioddays}
                                />
                            </Text>
                        </div>
                    </td>
                )}
                {actualActive && showPlanned && gridInputType !== 'percent' && (
                    <td className={`rowTotalContainer noselect`}>
                        <div className="rowTotal totalCell">
                            <Text className="totalCellInner" as="div">
                                <DisplayCell value={times?.reduce((prev, cur) => prev + (cur.actualWork || 0), 0)} periodDays={props.perioddays} />
                            </Text>
                        </div>
                    </td>
                )}
                {remainingActive && showPlanned && gridInputType !== 'percent' && (
                    <td className={`rowTotalContainer noselect`}>
                        <div className="rowTotal totalCell">
                            <Text className="totalCellInner" as="div">
                                <DisplayCell value={times?.reduce((prev, cur) => prev + (cur.remainingWork || 0), 0)} periodDays={props.perioddays} />
                            </Text>
                        </div>
                    </td>
                )}
                <td className="rowTotalContainer noselect">
                    <div className="rowTotal totalCell">
                        <Text as="div" className="totalCellInner">
                            <DisplayCell
                                value={GetTotalFromTimeRowArray(times) != null ? FormatValue(GetTotalFromTimeRowArray(times)) : ''}
                                periodDays={workingDays}
                            />
                        </Text>
                    </div>
                </td>
            </tr>
            {/* </ReactCSSTransitionGroup> */}
            {times?.map((time: UserRegistrationPeriod, index) => {
                if (!expanded) {
                    return null;
                }

                return (
                    <tr key={time.assignmentId} className={`subRow${index === times?.length - 1 ? ' lastSubRow' : ''}`}>
                        <td className="rowHeader noselect">
                            <Stack horizontal className="rowHeaderContainer">
                                <div className="expander" />
                                <Stack.Item grow>
                                    <TooltipHost directionalHint={DirectionalHint.topLeftEdge} content={time.taskDescription} closeDelay={250}>
                                        <Text className="subGroupName textOverflow">
                                            {time.taskName.length < 70 ? (
                                                time.taskName
                                            ) : (
                                                <TooltipHost content={time.taskName}>{time.taskName.substr(0, 68) + '...'}</TooltipHost>
                                            )}
                                        </Text>
                                    </TooltipHost>
                                </Stack.Item>

                                <Stack.Item className="noselect rowDelete">
                                    <MyTimeDelete Time={time} deleteMyTime={DeleteMyTime} enabled={!timesheetIsReadonly} />
                                </Stack.Item>

                                <Stack.Item className="noselect pin">
                                    <MyTimeAdd key={index} pinned={time.pinned} Time={time} pinMyTime={PinMyTime} isMyTime={true} />
                                </Stack.Item>
                            </Stack>
                        </td>

                        {workTypesEnabled && (
                            <td className="cellContainer worktypeContainer noselect">
                                <div className="cell day">
                                    <WorkTypePicker
                                        readOnly={timesheetIsReadonly || time.projectClosed || time.taskClosed}
                                        row={time}
                                        existingTaskTimeRows={times}
                                        onChange={props.updateWorkType}
                                    />
                                </div>
                            </td>
                        )}
                        {plannedActive && showPlanned && gridInputType !== 'percent' && (
                            <td className={`cellContainer noselect`}>
                                <div className="cell day">
                                    <PlannedCell time={time} periodDays={props.perioddays} useDisplayCell={true} />
                                </div>
                            </td>
                        )}
                        {actualActive && showPlanned && gridInputType !== 'percent' && (
                            <td className={`cellContainer noselect`}>
                                <div className="cell day">
                                    <DisplayCell value={time.actualWork} periodDays={props.perioddays} />
                                </div>
                            </td>
                        )}
                        {remainingActive && showPlanned && gridInputType !== 'percent' && (
                            <td className={`cellContainer`}>
                                <div className="cell day">
                                    {/* If not a default task, allow editing of remaining work */}
                                    {remainingWorkWritable && !time.taskIsDefault ? (
                                        <EditableRemainingWorkCell
                                            assignmentId={time.assignmentId}
                                            taskId={time.taskId}
                                            workTypeId={time.workType?.id}
                                            value={time.remainingWork || 0}
                                            periodDays={props.perioddays}
                                            readonly={timesheetIsReadonly || time.projectClosed || time.taskClosed || !remainingWorkWritable}
                                            onRemainingWorkUpdate={handleRemainingWorkUpdate}
                                        />
                                    ) : (
                                        <DisplayCell value={time.remainingWork || 0} periodDays={props.perioddays} />
                                    )}
                                </div>
                            </td>
                        )}
                        <td className="cellContainer noselect">
                            <div className="cell day">
                                <CellWeek
                                    time={time}
                                    timeentries={time.timeEntries}
                                    perioddays={props.perioddays}
                                    updated={onCellUpdated}
                                    reportPeriodId={props.reportPeriodId}
                                    readonly={getIfReadOnly(time) !== ''}
                                    readonlyreason={getIfReadOnly(time)}
                                    ref={n => cellCtx.addInputNode(time.taskId, time.workType?.id, 0, n)}
                                    cellDataNeedsSave={setCellDataNeedsSaveFunc}
                                />
                            </div>
                        </td>
                    </tr>
                );
            })}
        </>
    );
}

export default TimePeriodGroupWeek;
